import React, { useEffect, useMemo, useRef, useState } from "react";
import { localCellToBitIdx } from "../lib/time";

const TOTAL_AVAIL_SLOTS = 7 * 48;
const SLOTS_PER_DAY = 48;

// Monday-indexed weekday names. The grid is rotated out of this so its first
// column is the region's reset day.
const WEEKDAY_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;

/**
 * Column order for a week that starts on `resetWeekdayIso` (Mon=1 .. Sun=7).
 *
 * This is a PURE PRESENTATION ROTATION. The underlying 336-bit string is always
 * Monday-anchored and never moves: `localCellToBitIdx` maps whatever column the
 * user clicks back to its canonical bit, so reordering columns cannot corrupt
 * stored availability. That is what makes "EU sees Wed-first, US sees Tue-first"
 * a two-line change rather than a migration.
 *
 * EU (Wed, 3) -> ["Wed".."Tue"], order [2,3,4,5,6,0,1]  (the historical default)
 * US (Tue, 2) -> ["Tue".."Mon"], order [1,2,3,4,5,6,0]
 */
export function weekColumnsForReset(resetWeekdayIso: number): {
  daysDisplay: string[];
  displayDayOrderLocalMon0: number[];
} {
  const startMon0 = ((Math.trunc(resetWeekdayIso) - 1) % 7 + 7) % 7;
  const order = Array.from({ length: 7 }, (_, i) => (startMon0 + i) % 7);
  return {
    daysDisplay: order.map((d) => WEEKDAY_SHORT[d]),
    displayDayOrderLocalMon0: order,
  };
}

const EU_COLUMNS = weekColumnsForReset(3);

type AvailabilityGridProps = {
  week: boolean[];
  onChange: (next: boolean[]) => void;
  weekStartIso: string;
  userTimezone: string;
  daysDisplay?: readonly string[];
  displayDayOrderLocalMon0?: readonly number[];
  /**
   * ISO weekday the viewer's week starts on (Mon=1 .. Sun=7): 3 for EU, 2 for
   * US. Sets both column props at once. Explicit `daysDisplay` /
   * `displayDayOrderLocalMon0` still win, so existing callers are unaffected.
   */
  resetWeekdayIso?: number;
  compact?: boolean;
};

export default function AvailabilityGrid({
  week,
  onChange,
  weekStartIso,
  userTimezone,
  daysDisplay,
  displayDayOrderLocalMon0,
  resetWeekdayIso,
  compact = false,
}: AvailabilityGridProps) {
  const columns = useMemo(
    () => (resetWeekdayIso ? weekColumnsForReset(resetWeekdayIso) : EU_COLUMNS),
    [resetWeekdayIso]
  );
  const resolvedDaysDisplay = daysDisplay ?? columns.daysDisplay;
  const resolvedDayOrder = displayDayOrderLocalMon0 ?? columns.displayDayOrderLocalMon0;
  const [hoveredLabel, setHoveredLabel] = useState<string | null>(null);

  const safeWeek =
    week.length === TOTAL_AVAIL_SLOTS
      ? week
      : [...week, ...new Array(TOTAL_AVAIL_SLOTS - week.length).fill(false)];
  const [localWeek, setLocalWeek] = useState<boolean[]>(safeWeek);

  const isDraggingRef = useRef(false);
  const dragValueRef = useRef<boolean | null>(null);
  const localWeekRef = useRef<boolean[]>(safeWeek);

  useEffect(() => {
    if (!isDraggingRef.current) {
      const safe =
        week.length === TOTAL_AVAIL_SLOTS
          ? week
          : [...week, ...new Array(TOTAL_AVAIL_SLOTS - week.length).fill(false)];
      localWeekRef.current = [...safe];
      setLocalWeek(safe);
    }
  }, [week]);

  useEffect(() => {
    const stop = () => {
      if (isDraggingRef.current) {
        onChange([...localWeekRef.current]);
      }
      isDraggingRef.current = false;
      dragValueRef.current = null;
    };
    window.addEventListener("mouseup", stop);
    window.addEventListener("mouseleave", stop);
    return () => {
      window.removeEventListener("mouseup", stop);
      window.removeEventListener("mouseleave", stop);
    };
  }, [onChange]);

  const displayToDataIndex = (displayDayIdx: number, slotInDay: number): number => {
    // Display cells are in the user's local week view; data is UTC-indexed.
    const localDayMon0 = resolvedDayOrder[displayDayIdx] ?? displayDayIdx;
    return localCellToBitIdx(weekStartIso, localDayMon0, slotInDay, userTimezone);
  };

  const getLabel = (displayDayIdx: number, slotInDay: number) => {
    const day = resolvedDaysDisplay[displayDayIdx] || "";
    const hour = Math.floor(slotInDay / 2);
    const min = (slotInDay % 2) * 30;
    const time = `${hour.toString().padStart(2, "0")}:${min.toString().padStart(2, "0")}`;
    return `${day} @ ${time}`;
  };

  const slotToTime = (slot: number) => {
    const hour = Math.floor(slot / 2);
    const min = (slot % 2) * 30;
    return `${hour.toString().padStart(2, "0")}:${min.toString().padStart(2, "0")}`;
  };

  // Contiguous runs of available half-hours per display day — computed from the
  // same local->data mapping the cells use, so labels and the summary can't drift.
  const runsByDisplayDay = useMemo(
    () =>
      resolvedDaysDisplay.map((_, displayDayIdx) => {
        const runs: { start: number; end: number }[] = [];
        let runStart = -1;
        for (let s = 0; s < SLOTS_PER_DAY; s++) {
          const on = localWeek[displayToDataIndex(displayDayIdx, s)];
          const isLast = s === SLOTS_PER_DAY - 1;
          if (on && runStart === -1) runStart = s;
          if (runStart !== -1 && (!on || isLast)) {
            runs.push({ start: runStart, end: on && isLast ? s + 1 : s });
            runStart = -1;
          }
        }
        return runs;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [localWeek, weekStartIso, userTimezone, resolvedDayOrder, resolvedDaysDisplay],
  );

  const handleInteract = (dataIdx: number, isStart: boolean) => {
    if (isStart) {
      isDraggingRef.current = true;
      const nextVal = !localWeekRef.current[dataIdx];
      dragValueRef.current = nextVal;
      localWeekRef.current[dataIdx] = nextVal;
      setLocalWeek([...localWeekRef.current]);
    } else if (
      isDraggingRef.current &&
      dragValueRef.current !== null &&
      localWeekRef.current[dataIdx] !== dragValueRef.current
    ) {
      localWeekRef.current[dataIdx] = dragValueRef.current;
      setLocalWeek([...localWeekRef.current]);
    }
  };

  const headerHeightClass = compact ? "h-4 mb-2" : "h-6 mb-2";
  const labelClass = compact
    ? "text-xs font-mono font-bold text-cyan-700 dark:text-cyan-400 transition-opacity"
    : "text-sm font-mono font-bold text-cyan-700 dark:text-cyan-400 transition-opacity";
    const hourTextClass = compact
    ? "absolute -left-1.5 -top-3 text-[10px] text-[var(--c-ink-3)] font-mono"
    : "absolute -left-2 -top-5 text-[13px] text-[var(--c-ink-2)] font-mono font-semibold tabular-nums";
    const dayLabelClass = compact
    ? "flex items-center justify-end pr-3 text-[11px]"
    : "flex items-center justify-end pr-3 text-sm tracking-wide";

  return (
    <div
      className="overflow-x-auto pb-2 select-none"
      onDragStart={(e) => e.preventDefault()}
      onMouseLeave={() => setHoveredLabel(null)}
    >
      <div className="min-w-[800px]">
      <div className={`flex justify-end ${headerHeightClass}`}>
          <div className={`${labelClass} ${hoveredLabel ? "opacity-100" : "opacity-0"}`}>{hoveredLabel}</div>
        </div>

        <div className={`grid grid-cols-[60px_repeat(48,1fr)] ${compact ? "mb-1" : "mb-2 mt-4"}`}>
          <div />
          {Array.from({ length: 48 }).map((_, i) => (
            <div key={i} className="h-3 relative border-l border-[var(--c-line-soft)]">
              {i % 4 === 0 && (
                <span className={hourTextClass}>{(i / 2).toString().padStart(2, "0")}</span>
              )}
            </div>
          ))}
        </div>

        {resolvedDaysDisplay.map((day, displayDayIdx) => {
          const dayRuns = runsByDisplayDay[displayDayIdx];
          const dayHasAvail = dayRuns.length > 0;
          return (
          <div key={`${day}-${displayDayIdx}`} className="grid grid-cols-[60px_repeat(48,1fr)] h-7 gap-[1px] mb-[1px]">
            <div className={`${dayLabelClass} ${dayHasAvail ? "font-bold text-[var(--c-ink)]" : "font-medium text-[var(--c-ink-3)]"}`}>{day}</div>
            {Array.from({ length: SLOTS_PER_DAY }).map((_, slotIdx) => {
              const dataIdx = displayToDataIndex(displayDayIdx, slotIdx);
              const active = localWeek[dataIdx];
              const isMajorBlock = slotIdx % 8 === 0;
              const run = dayRuns.find((r) => r.start === slotIdx);
              const runLen = run ? run.end - run.start : 0;
              return (
                <div
                  key={slotIdx}
                  onMouseDown={() => handleInteract(dataIdx, true)}
                  onMouseEnter={() => {
                    setHoveredLabel(getLabel(displayDayIdx, slotIdx));
                    handleInteract(dataIdx, false);
                  }}
                  className={`relative transition-colors duration-75 ${active ? "bg-cyan-500/25" : "bg-[var(--c-sunken)] hover:bg-[var(--c-sunken-2)]"} ${run ? "z-30" : active ? "z-10" : ""} ${isMajorBlock ? "border-l border-[var(--c-line)]" : ""}`}
                >
                  {run && (
                    <div
                      className="pointer-events-none absolute inset-y-0 left-0 z-20 flex items-center justify-center overflow-hidden rounded-[4px] bg-cyan-500"
                      style={{ width: `calc(${runLen * 100}% + ${runLen - 1}px)` }}
                    >
                      {!compact && runLen >= 5 && (
                        <span className="whitespace-nowrap px-1 text-xs font-bold tracking-wide text-cyan-950">
                          {slotToTime(run.start)} – {slotToTime(run.end)}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          );
        })}
      </div>
    </div>
  );
}
